import { SeverityLevel } from '@sentry/react';
import { AxiosError } from 'axios';
import { HeaderRequestFields } from '../../config';
import { Extras } from '@sentry/types/types/extra';
import { sentry } from '../../../sentry';

function development(level: SeverityLevel, ...args) {
    if (process.env.NODE_ENV === 'development') {
        console.log(`%c[sentry-${level}]`, `background-color: #ffffff; color: ${mapLevelToColor(level)}`, ...args);
    }
}

function mapLevelToColor(level: SeverityLevel): string {
    switch (level) {
        case 'error':
            return '#ff0000';
        case 'warning':
            return '#ff8c00';
        case 'info':
        default:
            return '#000000';
    }
}

function info(message: string, payload?: any) {
    development('info', message, payload);
    sentry.withScope(scope => {
        scope.setExtras(payload);
        sentry.captureMessage(message, 'info');
    });
}

function warning(message: string, payload?: any) {
    development('warning', message, payload);
    sentry.withScope(scope => {
        scope.setExtras(payload);
        sentry.captureMessage(message, 'warning');
    });
}

function error(message: string | Error | any, payload?: Extras) {
    development('error', message, payload);
    if (typeof message === 'boolean') {
        return;
    }
    if (typeof message === 'string') {
        sentry.captureEvent({
            message,
            extra: payload,
            level: 'error'
        });
    } else if (message instanceof Error) {
        sentry.withScope(scope => {
            if (payload) scope.setExtras(payload);
            sentry.captureException(message);
        });
    } else if (typeof message === 'object' && message?.isAxiosError) {
        axiosError(message);
    } else if (typeof message === 'object' && message?.runtimeDuplication) {
        // Ignore race condition errors of axios. They are old requests and the data is outdated but never used.
        // Can happen after rapid clicks where requests may overtake each other.
        return;
    } else {
        sentry.captureEvent({
            message: 'Encountered malformed error',
            extra: {
                ...payload,
                errorMessage: message
            },
            level: 'error'
        });
    }
}

function axiosError(axiosError: AxiosError) {
    development('error', 'Axios Error', axiosError);
    try {
        const statusCode = axiosError?.response?.status ?? -1;
        const message = `HTTP ${statusCode} - ${axiosError?.response?.data?.message}`;

        if (statusCode >= 500) {
            error(message, {
                statusCode,
                url: axiosError?.config?.url,
                statusText: axiosError?.response?.statusText,
                responseMessage: axiosError?.response?.data?.message
            });
        } else if (statusCode !== 409 && statusCode >= 400 && statusCode <= 499) {
            const axiosHeaders = axiosError?.response?.config?.headers;

            const requestRequestId =
                axiosHeaders && HeaderRequestFields.HEADER_BORROWBOX_REQUESTID in axiosHeaders
                    ? axiosHeaders[HeaderRequestFields.HEADER_BORROWBOX_REQUESTID]
                    : '';

            const payload = {
                'Request-RequestId': requestRequestId,
                'Response-RequestId': axiosError?.response?.headers[HeaderRequestFields.HEADER_BORROWBOX_REQUESTID],
                status: axiosError?.response?.status,
                statusText: axiosError?.response?.statusText
            };

            const statusCodesToSkip = axiosError?.config?.errorCodesToSkip ?? [];
            if (statusCodesToSkip.includes(statusCode)) {
                info(message, payload);
            } else {
                warning(message, payload);
            }
        }
    } catch (err) {
        error(err);
    }
}

export const log = {
    info,
    warning,
    error,
    axiosError
};
